/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, Grid } from '@mui/material';
import { DialogForm, ICardActions, ICardColumns, IDialogActions, ISnackbarAlert, SnackbarAlert } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { GenericCardList } from '../../share/generic-cruds/GenericCardList';
import { ListId, LocalStorange } from '../../../shared/environment';
import { useTheme } from '@mui/material/styles';
import { AccessRoleDTO, AccessRoleFilterDTO, CompanyPeopleDTO, CompanyPeopleFilterDTO } from '../../../shared/interfaces';
import { useTranslation } from 'react-i18next';

interface ICompanyPeopleAssociateProps {
    data: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const CompanyPeopleAssociate: React.FC<ICompanyPeopleAssociateProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {
    const [companyPeopleDTO, setCompanyPeopleDTO] = useState<CompanyPeopleDTO>();
    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);
    const theme = useTheme();

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setCompanyPeopleDTO({
            ...companyPeopleDTO,
            companyId: '',
            peopleId: '',
            createdById: ''
        } as CompanyPeopleDTO);
    }, []);

    useEffect(() => {
        handleGetPermission(ListId.corpsphere.companyPeopleAssociateId, setAccessRolePermissionDTO);
    }, [data]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList,);
    }, []);

    const HandleUserAddAsync = useCallback( async (companyPeople: any) => {
        const dto: CompanyPeopleDTO = {
            companyId: data.id,
            peopleId: companyPeople.peopleId,
            createdById: userInfo.user.id
        };
        GenericService.AddAsync1('corpsphere.companyPeople', dto, setCompanyPeopleDTO, setActionExecuted, setLoading, showMessage);
    }, [data]);

    const handleDeleteClick = useCallback ( async (companyPeople: any) => {
        const dto: CompanyPeopleDTO = {
            companyId: data.id,
            peopleId: companyPeople.peopleId,
            createdById: ''
        };
        GenericService.PostGenericAsync1('corpsphere.companyPeople', 'DeleteByAsync', dto, setActionExecuted, setLoading, showMessage);
    },[data]);

    const handleAssociateClick = (companyPeople: string) => {
        if(!accessRolePermissionDTO?.allowAdd) {
            showMessage(t('message.allowGetAllView'), 'info');
            return;
        }
        HandleUserAddAsync(companyPeople);
    };

    const handleDisassociate = (companyPeople: string) => {
        if(!accessRolePermissionDTO?.allowDelete) {
            showMessage(t('message.allowGetAllView'), 'info');
            return;
        }
        handleDeleteClick(companyPeople);
    };

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const filterSelected: CompanyPeopleFilterDTO = {
        companyId: data.id,
        peopleId: '',
        searchAssociated: true
    };

    const columnsSelected: ICardColumns[] = [
        { field: 'fullName', label: t('companyPeopleAssociate.columnsAssociated.userName'), size: 8, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'name', label: t('companyPeopleAssociate.columnsAssociated.company'), size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    const cardActionsSelected: ICardActions[] = [
        { id: 'disassociate', toolTip: t('companyPeopleAssociate.cardActionsAssociated.disassociate'), icon: 'delete_outlined', iconColor: 'error', setAction: handleDisassociate },
    ];

    const filterAvailable: CompanyPeopleFilterDTO = {
        companyId: data.id,
        peopleId: '',
        searchAssociated: false
    };

    const columnsAvailable: ICardColumns[] = [
        { field: 'fullName', label: t('companyPeopleAssociate.columnsAssociated.userName'), size: 8, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'name', label: t('companyPeopleAssociate.columnsAssociated.company'), size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    const cardActionsAvailable: ICardActions[] = [
        { id: 'associate', toolTip: t('companyPeopleAssociate.cardActionsAvailable.associate'), icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleAssociateClick },
    ];
    const actions: IDialogActions[] = [
        { id: 'close', text: t('companyPeopleAssociate.maintenanceButton.close'), icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title={t('companyPeopleAssociate.detailsName') + ' TO ' + data.name}
                icon='compare_arrows_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'xl'}
                actions={actions}
                loading={false}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Card sx={{ bgcolor: theme.palette.background.default, p: 1, height: '58vh' }}>
                                <GenericCardList
                                    title={t('companyPeopleAssociate.gridTitleAssociated')}
                                    cardActions={cardActionsSelected}
                                    cardColumns={columnsSelected}
                                    entity={'corpsphere.companypeople'}
                                    filter={filterSelected}
                                    loading1={loading}
                                    hasChildren={false}
                                    setExpandedData={() => void undefined}
                                />
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card sx={{ bgcolor: theme.palette.background.default, p: 1, height: '58vh' }}>
                                <GenericCardList
                                    title={t('companyPeopleAssociate.gridTitleAvailable')}
                                    cardActions={cardActionsAvailable}
                                    cardColumns={columnsAvailable}
                                    entity={'corpsphere.companypeople'}
                                    filter={filterAvailable}
                                    loading1={loading}
                                    hasChildren={false}
                                    setExpandedData={() => void undefined}
                                />
                            </Card>
                        </Grid>
                    </Grid>
                </React.Fragment>
            </DialogForm >
        </React.Fragment >
    );
};
